// The library's semantic search (spec S3) as pages: `AssetSearchPage {items, total, next}`, the
// brand's nearest images and video scenes to the words, best first, at most 256 in all. Orval
// writes no infinite query, so this one is built from the generated `searchAssets` and its key.
import { useInfiniteQuery } from "@tanstack/react-query";

import { getSearchAssetsQueryKey, searchAssets } from "../../api/generated/endpoints/assets/assets";
import type { Asset, AssetHit, SearchAssetsParams } from "../../api/generated/model";
import { libraryParams, type LibraryFilter } from "./library-pages";

/** The route's limit on the words (spec S3). */
export const ASSET_SEARCH_MAX = 500;

/** Live's page of hits (`limit` 20). */
const PAGE_SIZE = 20;

/**
 * How long a search's hits are kept fresh: each read embeds the words (a call to the model), so
 * one Enter makes one request, and a focus of the window reads nothing again. The hits' URLs last
 * about an hour (S1).
 */
export const SEARCH_FRESH_MS = 10 * 60 * 1000;

/**
 * The search's params: the words and the list's filters the search takes (spec S3: kind, folder,
 * source, generation type, product). Live's search ignores who made a file, the order and the
 * folders under the folder, so they are not sent.
 */
export function searchParams(q: string, filter: LibraryFilter): SearchAssetsParams {
  const list = libraryParams(filter);
  return {
    q,
    ...(list.kind === undefined ? {} : { kind: list.kind }),
    ...(list.folder_id === undefined ? {} : { folder_id: list.folder_id }),
    ...(list.source === undefined ? {} : { source: list.source }),
    ...(list.generation_type === undefined ? {} : { generation_type: list.generation_type }),
    ...(list.product_id === undefined ? {} : { product_id: list.product_id }),
  };
}

export function useAssetSearchPages(teamId: string, brandId: string, q: string, filter: LibraryFilter, enabled: boolean) {
  const params = searchParams(q, filter);
  return useInfiniteQuery({
    enabled,
    staleTime: SEARCH_FRESH_MS,
    refetchOnWindowFocus: false,
    queryKey: [...getSearchAssetsQueryKey(teamId, brandId, params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      searchAssets(
        teamId,
        brandId,
        { ...params, limit: PAGE_SIZE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
}

/** A hit's key in the grid: its asset, and for a video its scene, which may match apart. */
export function hitKey(hit: Pick<AssetHit, "asset" | "scene">): string {
  return hit.scene === null ? hit.asset.id : `${hit.asset.id}@${hit.scene.start_ms}`;
}

/** The hits' files, each once, in their first hit's place: what the selection and the bulk bar
 * act on (the preview steps through the hits themselves). */
export function hitAssets(hits: readonly AssetHit[]): Asset[] {
  const seen = new Set<string>();
  return hits.flatMap((hit) => {
    if (seen.has(hit.asset.id)) return [];
    seen.add(hit.asset.id);
    return [hit.asset];
  });
}
